import { useCallback, useEffect } from 'react'
import lftDarkArrow from '../../../assets/images/prev.png'
import rtDarkArrow from '../../../assets/images/next.png'
import lftArrow from '../../../assets/images/lft-arrow.png'
import rtArrow from '../../../assets/images/rt-arrow.png'

export const useCarousel = () => {
  // Responsive breakpoint configuration
  const getItemsPerScreen = useCallback(() => {
    const width = window.innerWidth
    switch (true) {
      case width > 991:
        return 4
      case width > 767 && width <= 991:
        return 3
      case width > 575 && width < 768:
        return 2
      default:
        return 1
    }
  }, [])

  // Update arrow states based on current position
  const updateArrowStates = useCallback(
    (leftArrow, rightArrow, sliderIdx, progressBarTickCount) => {
      if (sliderIdx === 0 && progressBarTickCount === 1) {
        leftArrow.classList.remove('active')
        leftArrow.src = lftArrow
        rightArrow.classList.remove('active')
        rightArrow.src = rtArrow
      } else if (sliderIdx === 0 && progressBarTickCount > 1) {
        leftArrow.classList.remove('active')
        leftArrow.src = lftArrow
        rightArrow.classList.add('active')
        rightArrow.src = rtDarkArrow
      }
    },
    []
  )

  // Update arrow states during scrolling
  const updateScrollArrowStates = useCallback(
    (leftArrow, rightArrow, newIdx, progressBarTicks) => {
      if (newIdx === 0 && progressBarTicks > 1) {
        leftArrow.classList.remove('active')
        leftArrow.src = lftArrow
        rightArrow.classList.add('active')
        rightArrow.src = rtDarkArrow
      } else if (newIdx === progressBarTicks - 1) {
        leftArrow.classList.add('active')
        leftArrow.src = lftDarkArrow
        rightArrow.classList.remove('active')
        rightArrow.src = rtArrow
      } else if (newIdx > 0 && newIdx < progressBarTicks - 1) {
        leftArrow.classList.add('active')
        leftArrow.src = lftDarkArrow
        rightArrow.classList.add('active')
        rightArrow.src = rtDarkArrow
      }
    },
    []
  )

  // Calculate progress bar for a specific carousel
  const calculateProgressBar = useCallback(
    progressBar => {
      if (!progressBar) return

      progressBar.innerHTML = ''
      const slider =
        progressBar.closest('.slider-header').nextSibling.children[1]
          .children[0]
      const itemCount = slider.children.length
      const itemsPerScreen = getItemsPerScreen()

      slider.style.setProperty('--items-per-view', itemsPerScreen)

      const progressBarTickCount = Math.ceil(itemCount / itemsPerScreen)
      const cs = getComputedStyle(slider)
      const sliderIdx = parseInt(cs.getPropertyValue('--slider-index'))

      const leftArrow = slider
        .closest('.main-level01-row')
        .querySelector('.lft-arrow > .scroll-arrow')
      const rightArrow = slider
        .closest('.main-level01-row')
        .querySelector('.rt-arrow > .scroll-arrow')

      // Update arrow states
      updateArrowStates(leftArrow, rightArrow, sliderIdx, progressBarTickCount)

      // Create progress ticks
      for (let x = 0; x < progressBarTickCount; x++) {
        const tickItem = document.createElement('div')
        tickItem.classList.add('progress-tick')
        if (x === sliderIdx) {
          tickItem.classList.add('active')
        }
        progressBar.append(tickItem)
      }
    },
    [getItemsPerScreen, updateArrowStates]
  )

  // Handle scroll navigation
  const handleRowScroll = useCallback(
    (e, direction) => {
      let slider = e.target.closest('.scroll-container')

      const leftArrow = slider
        .closest('.main-level01-row')
        .querySelector('.lft-arrow > .scroll-arrow')
      const rightArrow = slider
        .closest('.main-level01-row')
        .querySelector('.rt-arrow > .scroll-arrow')
      const progressBar =
        slider.closest('.main-level01-row').previousSibling.children[1]

      // Get the actual slider element
      if (direction === 'left') {
        slider = slider.nextSibling.querySelector('.thumb-slider')
      } else {
        slider = slider.previousSibling.querySelector('.thumb-slider')
      }

      const cs = getComputedStyle(slider)
      const sliderIdx = parseInt(cs.getPropertyValue('--slider-index'))
      const itemsPerView = parseInt(cs.getPropertyValue('--items-per-view'))
      const widthPerBlock = parseInt(cs.getPropertyValue('--width-per-block'))
      const progressBarTicks = progressBar.children.length

      if (direction === 'left' && sliderIdx > 0) {
        const newIdx = sliderIdx - 1
        const totalTranslatex = newIdx * itemsPerView * widthPerBlock + 'px'

        slider.style.setProperty('--slider-index', newIdx)
        slider.style.setProperty('--total-translatex', totalTranslatex)

        // Update progress bar
        progressBar.children[sliderIdx]?.classList.remove('active')
        progressBar.children[newIdx]?.classList.add('active')

        // Update arrow states
        updateScrollArrowStates(leftArrow, rightArrow, newIdx, progressBarTicks)
      } else if (direction === 'right' && sliderIdx + 1 < progressBarTicks) {
        const newIdx = sliderIdx + 1
        const totalTranslatex = newIdx * itemsPerView * widthPerBlock + 'px'

        slider.style.setProperty('--slider-index', newIdx)
        slider.style.setProperty('--total-translatex', totalTranslatex)

        // Update progress bar
        progressBar.children[sliderIdx]?.classList.remove('active')
        progressBar.children[newIdx]?.classList.add('active')

        // Update arrow states
        updateScrollArrowStates(leftArrow, rightArrow, newIdx, progressBarTicks)
      }
    },
    [updateScrollArrowStates]
  )

  // Initialize all progress bars on the page
  const initializeProgressBars = useCallback(() => {
    document.querySelectorAll('.progress-bar').forEach(calculateProgressBar)
  }, [calculateProgressBar])

  // Reset all sliders to initial position
  const resetSliders = useCallback(() => {
    const sliders = document.querySelectorAll('.thumb-slider')
    const leftArrows = document.querySelectorAll('.lft-arrow')

    sliders.forEach(slider => {
      slider.style.setProperty('--slider-index', '0')
      slider.style.setProperty('--total-translatex', '0px')
    })

    leftArrows.forEach(arrow => {
      arrow.classList.remove('active')
      arrow.style.opacity = '0.5'
    })
  }, [])

  // Handle window resize to recalculate responsive layouts
  useEffect(() => {
    const handleResize = () => {
      initializeProgressBars()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [initializeProgressBars])

  return {
    calculateProgressBar,
    handleRowScroll,
    initializeProgressBars,
    resetSliders,
    getItemsPerScreen
  }
}
